import React, { memo } from 'react';

import PropTypes from 'prop-types';

import localization from '../../../../shared/strings';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import Brand from '../../Vendor';
import { aggregatedStringFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { mapAggregatedFieldToInputProps } from '../helpers/mapAggregatedFieldToInputProps';
import { resolveAggregatedFieldTooltip } from '../helpers/resolveAggregatedFieldTooltip';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import AggregatedScalarField from './AggregatedScalarField';
import styles from './AggregatedBrand.module.scss';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;

const AggregatedBrand = ({
  field,
  readOnly,
  readOnlyTooltip,
  isDiff,
}) => {
  const fieldLabel = l10n.autoMappedFieldLabels.vendor;
  const bindings = useBindAggregatedField('vendor', field, fieldLabel);
  const inputProps = mapAggregatedFieldToInputProps(field);

  if (!field || field.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return null;

  const isMultiple = field.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const isReadOnly = readOnly || inputProps.readOnly || isMultiple;
  const value = field.state === AGGREGATED_FIELD_STATE.COMMON ? (field.value ?? '') : '';
  const canSet = !readOnly && !inputProps.readOnly;
  const canClear = canSet && (isMultiple || Boolean(value));
  const tooltip = resolveAggregatedFieldTooltip({
    field,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    canSet,
    canClear,
  });

  if (isMultiple) {
    return (
      <AggregatedScalarField
        label={fieldLabel}
        value=""
        placeholder={aggregatedL10n.multipleValues}
        readOnly={readOnly}
        tooltip={tooltip}
        showClearButton={!readOnly}
        onChange={readOnly ? undefined : bindings.onChange}
        onClear={readOnly ? undefined : bindings.onClear}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <Brand
        value={value}
        readOnly={isReadOnly}
        readOnlyTooltip={readOnly ? readOnlyTooltip : null}
        tooltip={tooltip}
        isDiff={isDiff}
        onChange={isReadOnly ? undefined : bindings.onChange}
      />
    </div>
  );
};

AggregatedBrand.propTypes = {
  field: aggregatedStringFieldPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  isDiff: PropTypes.bool,
};

AggregatedBrand.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
  isDiff: false,
};

export default memo(AggregatedBrand);
